import { create } from 'zustand'
import type {} from '@redux-devtools/extension' // required for devtools typing
import { devtools } from 'zustand/middleware'
import type { BlockMessage } from '../types'

type MessagesStore = {
  messages: BlockMessage[]
  setMessages: (messages: BlockMessage[]) => void

  /**
   * Upserts a message into the store.
   *
   * Match priority:
   * 1. By `metadata.clientMessageId` — resolves optimistic messages with their server confirmation
   * 2. By `id` — deduplicates server-sent messages (e.g. duplicate SSE events)
   * 3. No match — appends the message
   *
   * On match, merges `{ ...existing, ...message }`.
   */
  saveMessage: (message: BlockMessage) => void
  /**
   * Patches specific fields on an existing message matched by `id`.
   *
   * Unlike `saveMessage` which upserts a full message, this applies a partial
   * update to a single message. Used for feedback, status changes, etc.
   *
   * No-ops silently if the message is not found.
   */
  updateMessage: (messageId: string, updates: Partial<BlockMessage>) => void
  removeMessage: (messageId: string) => void
  clearMessages: () => void
}

const createMessagesStore = (storeName: string) =>
  create<MessagesStore>()(
    devtools(
      (set) => ({
        messages: [],
        setMessages: (messages) => set({ messages }),
        saveMessage: (message) =>
          set(({ messages }) => {
            const clientMessageId = message.metadata?.clientMessageId
            let existingIndex = -1

            if (clientMessageId) {
              existingIndex = messages.findIndex((m) => m.metadata?.clientMessageId === clientMessageId)
            }

            if (existingIndex === -1) {
              existingIndex = messages.findIndex((m) => m.id === message.id)
            }

            if (existingIndex !== -1) {
              return {
                messages: [
                  ...messages.slice(0, existingIndex),
                  { ...messages[existingIndex], ...message },
                  ...messages.slice(existingIndex + 1),
                ],
              }
            }

            return { messages: [...messages, message] }
          }),

        updateMessage: (messageId, updates) =>
          set(({ messages }) => ({
            messages: messages.map((m) => (m.id === messageId ? { ...m, ...updates } : m)),
          })),

        removeMessage: (messageId) =>
          set(({ messages }) => ({
            messages: messages.filter((m) => m.id !== messageId),
          })),

        clearMessages: () => set({ messages: [] }),
      }),
      {
        name: storeName,
      }
    )
  )

type BoundMessagesStore = ReturnType<typeof createMessagesStore>

const messagesStoreInstances: { [key: string]: BoundMessagesStore } = {}

export const getUseMessagesStore = (conversationId: string): BoundMessagesStore => {
  const storeKey = `messages-${conversationId}`

  if (!messagesStoreInstances[storeKey]) {
    messagesStoreInstances[storeKey] = createMessagesStore(storeKey)
  }

  return messagesStoreInstances[storeKey]
}

/**
 * Clears all cached message store instances.
 * Useful for testing to ensure clean state between test runs.
 */
export const clearMessagesStoreCache = () => {
  Object.keys(messagesStoreInstances).forEach((key) => {
    delete messagesStoreInstances[key]
  })
}
